import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Typography, Button, Alert, CircularProgress } from '@mui/material';
import AdminPage from './pages/AdminPage';
import WorkflowEditorPage from './pages/WorkflowEditorPage';
import StoryflowEditorPage from './pages/StoryflowEditorPage';
import PlayPage from './pages/PlayPage';
import GameShell from './game/GameShell';
import DocsPage from './pages/DocsPage';
import TelegramLoginWidget from './components/TelegramLoginWidget';
import TypingHero from './components/TypingHero';
import { useAuthContext } from './context/AuthContext';
import { API_BASE_URL } from './utils/api';
import { logWarn } from './utils/log';

async function fetchWithTimeout(url, options = {}, ms = 20000) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(tid);
  }
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#8b5cf6' },
    secondary: { main: '#e879f9' },
    background: { default: '#09090b', paper: '#18181b' },
    divider: 'rgba(255,255,255,0.08)'
  },
  typography: {
    fontSize: 13,
    fontFamily: '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h3: { fontSize: '1.5rem', fontWeight: 600 },
    h5: { fontSize: '1.05rem', fontWeight: 600 },
    h6: { fontSize: '0.95rem', fontWeight: 600 },
    body1: { fontSize: '0.8125rem' },
    body2: { fontSize: '0.8125rem' }
  },
  shape: { borderRadius: 8 },
  transitions: {
    duration: {
      shortest: 75,
      shorter: 100,
      short: 125,
      standard: 150,
      complex: 200,
      enteringScreen: 150,
      leavingScreen: 100
    }
  },
  components: {
    MuiMenu: {
      styleOverrides: {
        /* Select dropdown scrolls this Paper (not MenuList); keeps dark thumb vs system white bar */
        paper: {
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 6
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.35)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' }
      }
    },
    MuiButton: {
      defaultProps: { size: 'small' },
      styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } }
    },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiChip: { defaultProps: { size: 'small' } }
  }
});

function AppContent() {
  const { getToken, signOut } = useAuthContext();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) {
          setUserRole(null);
          return;
        }

        const meRes = await fetchWithTimeout(`${API_BASE_URL}/api/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (meRes.ok) {
          const userData = await meRes.json();
          const role = userData.role;
          if (role === 'admin' || role === 'member') {
            setUserRole(role);
            return;
          }

          const initRes = await fetchWithTimeout(`${API_BASE_URL}/api/users/initialize-first-admin`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (initRes.ok) {
            const initData = await initRes.json();
            setUserRole(
              initData.role === 'admin' || initData.role === 'member' ? initData.role : 'admin'
            );
            return;
          }
          setUserRole(null);
          return;
        }

        if (meRes.status === 401) {
          signOut();
        }
        console.error('Failed to fetch user role:', meRes.status);
        setUserRole(null);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole(null);
        if (error?.name === 'AbortError') {
          console.warn('Role fetch timed out — check API / network');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [getToken, signOut]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-zinc-950 text-zinc-300">
        <CircularProgress size={28} sx={{ color: '#a78bfa' }} />
        <p className="text-sm">Loading…</p>
      </div>
    );
  }

  const hasAccess = userRole === 'admin' || userRole === 'member';

  return (
    <>
      {hasAccess ? (
        <Routes>
          <Route path="/workflows/:workflowId" element={<WorkflowEditorPage />} />
          <Route path="/storyflow/:storyflowId" element={<StoryflowEditorPage />} />
          <Route path="/play/:teamId" element={<PlayPage />} />
          <Route path="*" element={<AdminPage userRole={userRole} />} />
        </Routes>
      ) : (
        <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-950">
          <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 shadow-2xl shadow-black/40">
            <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(59,130,246,0.12)', color: '#e4e4e7' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#fafafa' }}>
                Access pending
              </Typography>
              <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                Your account is waiting for approval. An administrator needs to grant you member access.
              </Typography>
            </Alert>
            <p className="text-xs text-zinc-500 mb-4">Contact an administrator to get access.</p>
            <Button
              variant="outlined"
              fullWidth
              onClick={async () => {
                try {
                  const token = await getToken();
                  const response = await fetch(`${API_BASE_URL}/api/users/initialize-first-admin`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  if (response.ok) {
                    window.location.reload();
                  } else {
                    const error = await response.json();
                    alert(error.message || 'Failed to initialize admin');
                  }
                } catch (err) {
                  alert('Error: ' + err.message);
                }
              }}
            >
              Try initialize as first admin
            </Button>
            <Button variant="text" fullWidth sx={{ mt: 1.5 }} color="inherit" onClick={() => signOut()}>
              Log out
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function AdminGate() {
  const { isLoaded, isSignedIn, authError, clearAuthError, setSessionToken } = useAuthContext();
  const [gateCfg, setGateCfg] = useState(null);
  const [gateCfgError, setGateCfgError] = useState(null);
  const [localDevDone, setLocalDevDone] = useState(false);

  useEffect(() => {
    if (!isLoaded || isSignedIn) return undefined;
    let cancelled = false;
    setGateCfg(null);
    setGateCfgError(null);
    setLocalDevDone(false);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/telegram/config`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Auth config failed');
        if (cancelled) return;
        setGateCfg({
          botUsername: data.botUsername || null,
          allowLocalDevBypass: Boolean(data.allowLocalDevBypass)
        });
      } catch (e) {
        if (!cancelled) {
          const msg = e.message || 'Auth config failed';
          setGateCfgError(msg);
          logWarn('auth-gate', 'GET /api/auth/telegram/config:', msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    if (!gateCfg || isSignedIn) return undefined;
    if (!gateCfg.allowLocalDevBypass) {
      setLocalDevDone(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/telegram/local-dev-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.access_token) {
          setSessionToken(data.access_token);
        }
      } catch (e) {
        logWarn('auth-gate', 'POST /api/auth/telegram/local-dev-session:', e?.message || e);
      } finally {
        if (!cancelled) setLocalDevDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gateCfg, isSignedIn, setSessionToken]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-zinc-950 text-zinc-300">
        <CircularProgress size={28} sx={{ color: '#a78bfa' }} />
        <p className="text-sm">Loading…</p>
      </div>
    );
  }

  if (!isSignedIn) {
    const waitingGate = gateCfg === null && !gateCfgError;
    const waitingLocal = gateCfg && gateCfg.allowLocalDevBypass && !localDevDone;

    if (waitingGate || waitingLocal) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-zinc-300">
          <CircularProgress size={28} sx={{ color: '#a78bfa' }} />
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 bg-zinc-950" style={{ paddingBottom: '12vh' }}>
        {authError ? (
          <Alert
            severity="error"
            onClose={clearAuthError}
            sx={{ maxWidth: 'min(100%, 24rem)', width: '100%' }}
          >
            {decodeURIComponent(authError)}
          </Alert>
        ) : null}
        {gateCfgError ? (
          <Alert severity="error" sx={{ maxWidth: 'min(100%, 24rem)', width: '100%' }}>
            {gateCfgError}
          </Alert>
        ) : null}
        <TypingHero />
        <TelegramLoginWidget botUsername={gateCfg && !gateCfgError ? gateCfg.botUsername : null} />
      </div>
    );
  }

  return <AppContent />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route path="/docs" element={<DocsPage />} />
          {/* GameShell uses h-full/w-full (required so the Tauri standalone
             build clamps to the portrait aspect frame). The main SPA build
             has no sized parent for #root, so here we give it one: a fixed
             viewport-sized box. Without this the game collapses to 0px height
             when opened inside the admin iframe preview. */}
          <Route path="/game" element={
            <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', backgroundColor: '#000' }}>
              <GameShell />
            </div>
          } />
          <Route path="/*" element={<AdminGate />} />
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
